import { inject, ref, computed, nextTick, readonly } from "vue";
import { VUE_ROOT_APP_SYMBOL, PAGE_APP_SYMBOL, EMBED_APP_SYMBOL } from "_Main/injectionSymbols";
import { useStore } from "vuex";
import { useResource } from "Util/i18n";
import { globalGenerator } from "Util/uniqueIdGenerator";
import {
    downloadFile,
    getAnonParams,
    DOCUMENT_WRAPPER_TYPE,
    XHR_CONTENT_TYPE,
    IS_DEBUG_BUILD,
    loadDynamicScript,
    getDomainName
} from "Util/util.js";
import { ServiceController } from "Util/serviceController";
import {
    PROJECT_FORM_CONTROLLER_NAMESPACE,
    PROJECT_FORM_CONTROLLER_TYPES,
    SUBMIT_PROJECT,
    CONFIRM_ADMIN_SAVE,
    SAVE_FORM_DATA,
    SAVE_CONDITIONAL_DEPS,
    CALC_PV_OUTPUT,
    CALC_ENERGY_STORAGE_OUTPUT,
    GET_INCENTIVE_DESIGN_OPTIONS,
    UPDATE_INCENTIVE_SELECTOR_CONDITIONAL_DEPS,
    CALC_INCENTIVE,
    OVERRIDE_INCENTIVE,
    GENERATE_DOCUMENT,
    UPLOAD_ATTACHMENT,
    UPLOAD_ATTACHMENT_INTERNAL,
    REMOVE_ATTACHMENT,
    SET_SIGNATURE_CHOICE,
    GENERATE_SIGNATURE_DOCUMENT,
    REQUEST_SIGNATURES,
    CANCEL_ALL_SIGNATURES,
    CANCEL_INCOMPLETE_SIGNATURES,
    RESEND_SIGNATURE_NOTIFICATIONS,
    REQUEST_SIGNATURES_V2,
    RESEND_ESIGNATURE_NOTIFICATIONS_V2,
    VOID_SIGNATURES_V2,
    FETCH_ENVELOPE_UPDATE,
    CREATE_WET_SIGN_ENVELOPE,
    GENERATE_ENVELOPE_ITEM_DOCUMENT,
    UPLOAD_WET_SIGN_ENVELOPE_ITEM,
    REMOVE_WET_SIGN_ENVELOPE_ITEM,
    VIEW_ENVELOPE_ITEM,
    VIEW_COMPLETED_ENVELOPE_ATTACHMENT,
    VIEW_WET_SIGN_ENVELOPE_ITEM,
    COMPLETE_WET_SIGN_ENVELOPE,
    START_OVER_ENVELOPE,
    CALL_WEB_ADAPTER,
    RETRIEVE_SENSITIVE_DATA,
    SHOW_MFA_DIALOG,
    ADMIN_CONFIRM_SAVE_FORM_DATA,
    WAIT_FOR_ACTIVE_SAVES,
    REGISTER_SAVE_PROMISE,
    UPLOAD_TABLE_FILE,
    IMPORT_PROJECT_TABLE_ROWS,
    CANCEL_PROJECT_TABLE_IMPORT,
    ADD_PROJECT_TABLE_ROW,
    GET_PROJECT_TABLE_DATA,
    EDIT_PROJECT_TABLE_ROW,
    VIEW_PROJECT_TABLE_ROW,
    REMOVE_PROJECT_TABLE_ROW,
    FIND_PROJECT_TABLE_EXISTING_PROJECT,
    ADD_PROJECT_TABLE_EXISTING_PROJECT,
    GET_CUSTOM_LIST_DATA_FULL,
    GET_CUSTOM_LIST_DATA_INITIAL,
    GET_CUSTOM_LIST_DATA_CHILDREN,
    GET_ADDRESS_AUTOCOMPLETE,
    GET_ADDRESS_EXPANSION_AUTOCOMPLETE,
    GET_ADDRESS_COUNTY_AUTOCOMPLETE,
    GET_LOCATION_SAS_TOKEN,
    createProjectFormControllerModule
} from "Forms/Components/ProjectForm/projectFormControllerStore";
import { PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { FORM_NAMESPACE, FORM_TYPES } from "SharedStores/Form/formStore";
import { registerPvSystemService } from "Forms/Components/Elements/pvsystem/pvSystemService";
import { PROJECT_SUBMITTED, PROJECT_TABLE_PARAM } from "Forms/Components/Elements/projectTableElement.vue";
import { registerEnergyStorageSystemService } from "Forms/Components/Elements/energystoragesystem/energyStorageSystemService";
import {
    PROJECT_FORM_NAMESPACE,
    PROJECT_FORM_MODE,
    SAVE_STATE_MUTATION,
    PROJECT_FORM_SAVE_STATE
} from "Forms/Components/ProjectForm/projectFormStore";
import { NAMESPACE as i18nNS, KEYS as i18nKeys } from "./i18n/editProjectBase.i18n";
import { useProjectLockStore } from "Projects/projectLockStore.js";

export function useEditProjectBase({ useEmbed = false }) {
    //// INJECT EXTERNAL DEPENDENCIES
    const _vueRootApp = inject(VUE_ROOT_APP_SYMBOL);
    const _serviceControllers = _vueRootApp.config.globalProperties.$serviceControllers;
    const _store = useStore();
    const _page = useEmbed ? inject(EMBED_APP_SYMBOL) : inject(PAGE_APP_SYMBOL);
    const _anonParams = useEmbed ? getAnonParams(_page.AnonToken, _page.AnonNonce) : null;
    const _projectLockStore = useProjectLockStore();
    const { adminConfirmSaveDialogTitle, adminConfirmSaveDialogMessage } = useResource(i18nKeys, i18nNS);

    //// VARIABLES
    const _isInitialized = ref(false);
    const canPromoteForm = ref(false);
    const _activeSave = ref(Promise.resolve());
    const _submitLock = ref(null);
    const removeEpayment = ref(false);

    // The edit project controller defaults to using the page controller,
    // but can be injected if it is loaded separately
    const _editProjectController = ref(_page);
    const setEditProjectController = (clientController) =>
        (_editProjectController.value = new ServiceController(clientController, _page));

    // In admin mode, prompt user the first time an out-of-band save
    // is triggered. Stop prompting after the admin confirms the save
    const adminSaveConfirmed = ref(false);

    const stripeLoadPromise = ref(null);

    const wistiaLoadPromise = ref(null);

    const _sasToken = ref(null);
    const _sasTokenPromise = ref(null);

    // These hold references to vue components (initialized in onEditProjectModelLoaded())
    const _projectForm = ref(null);
    const _mfaRequiredDialog = ref(null);

    const _uploadAttachmentUrl = ref(null);
    const _uploadTableFileUrl = ref(null);
    const _uploadWetSignEnvelopeItemUrl = ref(null);
    const _projectSubmittedUrl = ref(null);
    const _landingPageUrl = ref(null);
    const _internalLibraryUrl = ref(null);
    const _publicLibraryUrl = ref(null);

    //// COMPUTED
    const duringSubmit = computed(() => Boolean(_submitLock.value));
    const projectId = computed(() => (_isInitialized.value ? _store.state[PROJECT_NAMESPACE].ProjectId : null));
    const formVersionId = computed(() =>
        _isInitialized.value ? _store.state[FORM_NAMESPACE].FormVersionPublicId : null
    );
    const formId = computed(() => (_isInitialized.value ? _store.state[FORM_NAMESPACE].PublicId : null));
    const _useDraftStorage = computed(() => _store.state[PROJECT_NAMESPACE].UseDraftStorage);
    const _projectIsNewAnonVersa = computed(
        () => _store.state[PROJECT_NAMESPACE].IsAnonVersaProject && _store.state[PROJECT_NAMESPACE].IsNewProject
    );
    const _projectFormMode = computed(() => _store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode);
    const projectSaveState = computed(
        () => _store.state[PROJECT_FORM_NAMESPACE] && _store.state[PROJECT_FORM_NAMESPACE].saveState
    );
    const _isView = computed(
        () =>
            _projectFormMode.value === PROJECT_FORM_MODE.View ||
            _projectFormMode.value === PROJECT_FORM_MODE.Print ||
            _projectFormMode.value === PROJECT_FORM_MODE.AdminReadonly
    );
    const _isPreview = computed(() => _projectFormMode.value === PROJECT_FORM_MODE.Preview);
    const _isPrefill = computed(
        () =>
            _projectFormMode.value === PROJECT_FORM_MODE.Prefill ||
            _projectFormMode.value === PROJECT_FORM_MODE.PrefillAdmin
    );
    const _isReadOnly = computed(() => _projectLockStore.isLocked || _isView.value);
    const inAdminMode = computed(
        () =>
            _projectFormMode.value === PROJECT_FORM_MODE.Admin ||
            _projectFormMode.value === PROJECT_FORM_MODE.AdminReadonly
    );
    const _isOnAdminPage = computed(
        () =>
            _projectFormMode.value === PROJECT_FORM_MODE.Admin ||
            _projectFormMode.value === PROJECT_FORM_MODE.AdminReadonly ||
            _projectFormMode.value === PROJECT_FORM_MODE.AdminEdit
    );
    const hasAnyDefaultToApply = computed(() => _store.state[PROJECT_FORM_NAMESPACE].HasAnyDefaultToApply);
    const _formValues = computed(() => {
        // Filter down data fields to those that exist on the form
        const fieldValues = {};
        const dataFieldIds = _store.getters[FORM_TYPES.getters.dataFieldIdsOnForm];
        for (const dataFieldId of dataFieldIds) {
            fieldValues[dataFieldId] = _store.state[PROJECT_NAMESPACE].DataFields[dataFieldId];
        }
        return fieldValues;
    });

    //// METHODS
    function projectFormRef(element) {
        _projectForm.value = element;
    }

    function mfaRequiredDialogRef(element) {
        _mfaRequiredDialog.value = element;
    }

    function onEditProjectModelLoaded(model) {
        if (_isInitialized.value) {
            throw new Error("already initialized");
        }

        _projectLockStore.loadState(model.Project.ProjectLockState);

        const externalScriptOptions = model.ExternalScriptOptions;
        if (externalScriptOptions.LoadStripe) {
            stripeLoadPromise.value = loadDynamicScript("https://js.stripe.com/v3/");
        }

        if (externalScriptOptions.LoadWistia) {
            wistiaLoadPromise.value = loadDynamicScript("https://fast.wistia.com/assets/external/E-v1.js");
        }
        _uploadAttachmentUrl.value = model.Attachments && model.Attachments.UploadAttachmentUrl;
        _uploadTableFileUrl.value = model.Table && model.Table.UploadFileUrl;
        _uploadWetSignEnvelopeItemUrl.value = model.Envelopes && model.Envelopes.UploadWetSignItemUrl;
        _projectSubmittedUrl.value = model.ProjectSubmittedUrl;
        _landingPageUrl.value = _landingPageUrl.value || model.LandingPageUrl;
        _internalLibraryUrl.value = _internalLibraryUrl.value || model.InternalLibraryUrl;
        _publicLibraryUrl.value = _publicLibraryUrl.value || model.PublicLibraryUrl;

        // Add all form element controllers to servicecontrollers
        _serviceControllers.Attachments = new ServiceController(model.Attachments, _page);
        _serviceControllers.SensitiveData = new ServiceController(model.SensitiveData, _page);
        _serviceControllers.IncentiveSelector = new ServiceController(model.IncentiveSelector, _page);
        _serviceControllers.ContentLibrary = new ServiceController(model.ContentLibrary, _page);
        _serviceControllers.Envelopes = new ServiceController(model.Envelopes, _page);
        _serviceControllers.ProjectTable = new ServiceController(model.ProjectTable, _page);
        _serviceControllers.Table = new ServiceController(model.Table, _page);
        _serviceControllers.CustomList = new ServiceController(model.CustomList, _page);
        _serviceControllers.AddressAutocomplete = new ServiceController(model.AddressAutocomplete, _page);
        _serviceControllers.Location = new ServiceController(model.Location, _page);
        registerPvSystemService(_page, model.PvSystem, _serviceControllers);
        registerEnergyStorageSystemService(_page, model.EnergyStorageSystem, _serviceControllers);
        if (model.EPaymentDriver) {
            _serviceControllers.EPayments = new ServiceController(model.EPaymentDriver, _page);
        }

        // Register the project form controller store
        const controllerModule = createProjectFormControllerModule(_controllerMethods);
        _store.registerModule(PROJECT_FORM_CONTROLLER_NAMESPACE, controllerModule);

        _isInitialized.value = true;
        canPromoteForm.value = model.CanPromoteForm;
        removeEpayment.value = model.RemoveEpayment;

        // Initialize the projectForm
        _projectForm.value.initialize(model);
    }

    function _queueSave(saveDelegate, queueOptions) {
        if (_isReadOnly.value) {
            return _activeSave.value;
        }

        queueOptions = queueOptions || {};
        //while we are in a submit, ignore all operations unless they have the lock
        if (duringSubmit.value) {
            if (queueOptions.submitLock !== _submitLock.value) {
                return _activeSave.value;
            }
        }

        if (queueOptions.skipQueue) {
            return saveDelegate();
        }
        //this code serializes multiple consecutive save calls.
        _activeSave.value = (async () => {
            try {
                await _activeSave.value;
            } catch (e) {
                if (IS_DEBUG_BUILD) {
                    console.log(e);
                    console.log("Previous save failed, continuing with current save");
                }
            }

            return saveDelegate();
        })();
        return _activeSave.value;
    }

    async function _navigateAfterLoggedInSubmit(submitResult) {
        if (_isOnAdminPage.value) {
            // Don't do anything here. The parent page will handle reload
            return;
        }
        if (submitResult.ProjectNumberGenerated) {
            _page.setHref(_projectSubmittedUrl.value);
            await new Promise(() => {}); //halt this function until we actually change pages
        }
        _page.setHref(_landingPageUrl.value);
        await new Promise(() => {});
    }

    function dispatchSaveFormData(payload) {
        return _store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData, payload, {
            root: true
        });
    }

    async function _adminConfirmSaveFormData() {
        // Don't prompt user again if we've already prompted them
        if (adminSaveConfirmed.value) {
            return;
        }

        const yesResult = "YES";
        const noResult = "NO";

        const result = await _page.showDialog({
            title: adminConfirmSaveDialogTitle.value,
            message: adminConfirmSaveDialogMessage.value,
            buttons: [
                {
                    text: "No",
                    result: noResult
                },
                {
                    text: "Yes",
                    primary: true,
                    result: yesResult
                }
            ]
        });

        if (result === noResult) {
            return Promise.reject("Admin user declined to save changes and continue");
        }

        // Remember that we don't need to prompt user for future actions that will
        // need saves
        adminSaveConfirmed.value = true;
        return;
    }

    function beginLoadNewForm() {
        _projectForm.value.beginReload();
    }

    function onNewFormLoaded(model) {
        adminSaveConfirmed.value = false;
        canPromoteForm.value = model.CanPromoteForm;
        _projectForm.value.reinitialize(model);
    }

    function setValidationErrors(validationErrors) {
        _projectForm.value.setValidationErrors(validationErrors);
    }

    //// CONTROLLER METHODS
    // These are added to the project form controller store by `onEditProjectModelLoaded()`
    const _controllerMethods = {};
    _controllerMethods[WAIT_FOR_ACTIVE_SAVES] = () => {
        return _activeSave.value;
    };
    /**
     * Use this action to register a promise that should be added to the save queue.
     * Can be used to encapsulate larger multi step actions, like calculating pv output.
     * Whenever you use this, the actions that normally would be added to the queue need to be
     * manually excluded. this is done by passing a queueOptions object with skipQueue:true.
     * see calcPvOutput for an example.
     *
     * Caller must account for the possibility of an exception between registering this promise
     * and resolving it. In that case, this should be rejected, or it will remain pending in the
     * queue forever and will block any future queued saves.
     */
    _controllerMethods[REGISTER_SAVE_PROMISE] = (context, payload) => {
        if (payload instanceof Promise) {
            _queueSave(async () => {
                await payload;
            });
        }
    };
    _controllerMethods[SUBMIT_PROJECT] = async () => {
        if (_isPreview.value) {
            return;
        }
        let submitResult = null;
        const initialSaveState = projectSaveState.value;
        try {
            // If user explicitly submitting, don't show admin prompt
            adminSaveConfirmed.value = true;

            _submitLock.value = globalGenerator.getNextId("_submitLock");
            const queueOptions = { submitLock: _submitLock.value };

            // If unsaved changes, do a pre-save before submit unless in admin
            // mode. In admin mode, there's no draft so don't want to pre-save
            // in case it would save data that the form shouldn't allow to main
            if (!inAdminMode.value && initialSaveState === PROJECT_FORM_SAVE_STATE.HasUnsaved) {
                await dispatchSaveFormData({ queueOptions }, null);
            }
            _store.commit(SAVE_STATE_MUTATION, PROJECT_FORM_SAVE_STATE.Saving);
            const startResult = await _queueSave(() => {
                if (_projectIsNewAnonVersa.value && _editProjectController.value.StartNewVersaProjectSubmit2) {
                    return _editProjectController.value.StartNewVersaProjectSubmit2({
                        programId: _page.ProgramId,
                        projectId: projectId.value,
                        formVersionId: formVersionId.value,
                        fieldValues: _formValues.value,
                        useDraftStorage: _useDraftStorage.value,
                        isAdminSubmit: inAdminMode.value,
                        nonFormValuesList: _editProjectController.value.nonFormValuesList,
                        removeEpayment: removeEpayment.value
                    });
                }
                return _editProjectController.value.StartProjectSubmit2({
                    programId: _page.ProgramId,
                    projectId: projectId.value,
                    formVersionId: formVersionId.value,
                    fieldValues: _formValues.value,
                    useDraftStorage: _useDraftStorage.value,
                    isAdminSubmit: inAdminMode.value,
                    isOnAdminPage: _isOnAdminPage.value
                });
            }, queueOptions);
            const operationResult = await _page.waitForLongOperation({
                operationId: startResult.Data.OperationId,
                pollIntervalSeconds: 0.5,
                deleteWhenComplete: true
            });

            submitResult = JSON.parse(operationResult.Data.Output);
            const submitSuccess =
                !submitResult.HasError && (!submitResult.ValidationErrors || submitResult.ValidationErrors.length <= 0);

            if (submitSuccess) {
                _store.commit(SAVE_STATE_MUTATION, PROJECT_FORM_SAVE_STATE.Saved);

                // Send a project submitted message if we have a parent page in the same domain
                if (window.opener && document.referrer) {
                    const openerDomain = getDomainName(document.referrer);
                    const windowDomain = getDomainName(window.location.href);
                    const params = new URLSearchParams(window.location.search);
                    const projectTableId = params.get(PROJECT_TABLE_PARAM);
                    if (openerDomain === windowDomain) {
                        window.opener.postMessage(
                            { message: PROJECT_SUBMITTED, [PROJECT_TABLE_PARAM]: projectTableId },
                            new URL(document.referrer).origin
                        );
                    }
                }

                if (!useEmbed) {
                    await nextTick(); // lets the watcher run
                    await _navigateAfterLoggedInSubmit(submitResult);
                }
            }
            // Unable to submit
            else {
                // In admin mode, because we don't pre-save, need to revert to
                // initial save state before submit if submit failed. Otherwise
                // could give false impression that invalid changes were saved
                if (inAdminMode.value) {
                    _store.commit(SAVE_STATE_MUTATION, initialSaveState);
                }
                // In non-admin mode, even if we didn't submit successfully,
                // incomplete data gets pre-saved as draft data
                else {
                    _store.commit(SAVE_STATE_MUTATION, PROJECT_FORM_SAVE_STATE.Saved);
                }
            }
        } catch (e) {
            // If something goes wrong during save, revert to initial save state
            // rather than perpetually staying in "saving" state
            _store.commit(SAVE_STATE_MUTATION, initialSaveState);
            throw e;
        } finally {
            _submitLock.value = null;
        }

        return submitResult;
    };
    _controllerMethods[CONFIRM_ADMIN_SAVE] = () => {
        adminSaveConfirmed.value = true;
    };
    _controllerMethods[SAVE_FORM_DATA] = async (context, payload) => {
        if (_isPrefill.value) {
            return;
        }

        if (inAdminMode.value) {
            await _adminConfirmSaveFormData();
        }

        const queueOptions = (payload && payload.queueOptions) || {};
        _store.commit(SAVE_STATE_MUTATION, PROJECT_FORM_SAVE_STATE.Saving);

        return _queueSave(async () => {
            await _editProjectController.value.SaveChanges3({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formVersionId: formVersionId.value,
                fieldValues: _formValues.value,
                useDraftStorage: _useDraftStorage.value,
                isPreview: _isPreview.value
            });
            _store.commit(SAVE_STATE_MUTATION, PROJECT_FORM_SAVE_STATE.Saved);
        }, queueOptions);
    };
    _controllerMethods[SAVE_CONDITIONAL_DEPS] = async (context, { formElementId, groupDefinitionId }) => {
        return _queueSave(() => {
            return _editProjectController.value.SaveConditionalDependencies({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formElementId,
                groupDefinitionId
            });
        });
    };
    _controllerMethods[CALC_PV_OUTPUT] = async (context, { formElementId, queueOptions }) => {
        return _queueSave(() => {
            return _serviceControllers.PvSystem.CalculateOutput({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formElementId,
                formVersionId: formVersionId.value,
                useDraftStorage: _useDraftStorage.value
            });
        }, queueOptions);
    };
    _controllerMethods[CALC_ENERGY_STORAGE_OUTPUT] = async (context, { formElementId, queueOptions }) => {
        return _queueSave(() => {
            return _serviceControllers.EnergyStorageSystem.CalculateOutput({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formElementId,
                formVersionId: formVersionId.value,
                useDraftStorage: _useDraftStorage.value
            });
        }, queueOptions);
    };
    _controllerMethods[GET_INCENTIVE_DESIGN_OPTIONS] = (context, { formElementId, incentiveDesignId }) => {
        return _queueSave(() => {
            return _serviceControllers.IncentiveSelector.GetIncentiveDesignOptions({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formVersionId: formVersionId.value,
                formElementId: formElementId,
                incentiveDesignid: incentiveDesignId,
                useDraftStorage: _useDraftStorage.value
            });
        });
    };
    _controllerMethods[UPDATE_INCENTIVE_SELECTOR_CONDITIONAL_DEPS] = (
        context,
        { formElementId, incentiveDesignId }
    ) => {
        return _queueSave(() => {
            return _serviceControllers.IncentiveSelector.UpdateIncentiveSelectorConditionalDependencies({
                programPublicId: _page.ProgramId,
                projectPublicId: projectId.value,
                formElementPublicId: formElementId,
                incentiveDesignPublicId: incentiveDesignId
            });
        });
    };
    _controllerMethods[CALC_INCENTIVE] = (context, { formElementId, incentiveDesignId, queueOptions }) => {
        return _queueSave(() => {
            return _serviceControllers.IncentiveSelector.CalculateIncentive({
                programPublicId: _page.ProgramId,
                projectPublicId: projectId.value,
                formElementPublicId: formElementId,
                formVersionPublicId: formVersionId.value,
                incentiveDesignPublicId: incentiveDesignId,
                useDraftStorage: _useDraftStorage.value
            });
        }, queueOptions);
    };
    _controllerMethods[OVERRIDE_INCENTIVE] = (
        context,
        { formElementId, incentiveDesignId, overrideAmount, overrideReason, queueOptions }
    ) => {
        return _queueSave(() => {
            return _serviceControllers.IncentiveSelector.OverrideIncentive({
                programPublicId: _page.ProgramId,
                projectPublicId: projectId.value,
                formElementPublicId: formElementId,
                formVersionPublicId: formVersionId.value,
                incentiveDesignPublicId: incentiveDesignId,
                useDraftStorage: _useDraftStorage.value,
                overrideAmount: overrideAmount,
                overrideReason: overrideReason
            });
        }, queueOptions);
    };
    _controllerMethods[GENERATE_DOCUMENT] = async (context, { formElementId, templateDefinitionId }) => {
        if (_isReadOnly.value) {
            return;
        }
        await dispatchSaveFormData(context, null);
        const result = await _serviceControllers.Attachments.GenerateDocument({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formVersionId: formVersionId.value,
            formElementId: formElementId,
            templateDefinitionId: templateDefinitionId,
            useDraftStorage: _useDraftStorage.value
        });

        const documentId = result.Data.DocumentId;

        const href =
            _page.DownloadDocumentUrl +
            `?ProgramId=${_page.ProgramId}` +
            `&${DOCUMENT_WRAPPER_TYPE.DocumentId}=${documentId}`;
        downloadFile(href, _anonParams);
    };
    _controllerMethods[UPLOAD_ATTACHMENT] = (context, { file, formElementId, attachmentDefinitionId, captureXhr }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _queueSave(async () => {
            return await _controllerMethods[UPLOAD_ATTACHMENT_INTERNAL](context, {
                file,
                formElementId,
                attachmentDefinitionId,
                useDraftStorage: _useDraftStorage.value,
                captureXhr
            });
        });
    };
    _controllerMethods[UPLOAD_ATTACHMENT_INTERNAL] = async (
        context,
        { file, formElementId, attachmentDefinitionId, useDraftStorage, captureXhr }
    ) => {
        const data = {
            programId: _page.ProgramId,
            projectId: projectId.value,
            formElementId,
            formVersionId: formVersionId.value,
            attachmentDefinitionId,
            useDraftStorage: useDraftStorage,
            file
        };

        const result = await _page.sendXhr({
            url: _uploadAttachmentUrl.value,
            data,
            headers: _page.getXhrHeaders(),
            captureXhr,
            contentType: XHR_CONTENT_TYPE.FormData
        });

        return result.Data;
    };
    _controllerMethods[REMOVE_ATTACHMENT] = (context, { attachmentDefinitionId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _queueSave(() => {
            return _serviceControllers.Attachments.RemoveAttachment({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formVersionId: formVersionId.value,
                attachmentDefinitionId,
                useDraftStorage: _useDraftStorage.value
            });
        });
    };
    _controllerMethods[SET_SIGNATURE_CHOICE] = (context, { elementId, signatureChoice }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _queueSave(() => {
            return _serviceControllers.Attachments.SetSignatureChoice({
                programId: _page.ProgramId,
                projectId: projectId.value,
                formElementId: elementId,
                choice: signatureChoice
            });
        });
    };
    _controllerMethods[GENERATE_SIGNATURE_DOCUMENT] = async (context, { elementId }) => {
        if (_isReadOnly.value) {
            return;
        }
        const result = await _serviceControllers.Attachments.GenerateESignatureDocument({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formVersionId: formVersionId.value,
            formElementId: elementId,
            useDraftStorage: _useDraftStorage.value
        });
        const status = result.Data.SignatureStatus;

        const url =
            _page.DownloadDocumentUrl +
            `?ProgramId=${_page.ProgramId}` +
            `&${DOCUMENT_WRAPPER_TYPE.DocumentId}=${status.DocumentId}`;
        downloadFile(url, _anonParams);
        return status;
    };
    _controllerMethods[REQUEST_SIGNATURES] = () => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Attachments.RequestSignatures({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formVersionId: formVersionId.value,
            useDraftStorage: _useDraftStorage.value
        });
    };
    _controllerMethods[CANCEL_ALL_SIGNATURES] = (context, { explanationType, explanationDetail }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Attachments.CancelSignatures({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formId: formId.value,
            cancelAll: true,
            cancelExplanationType: explanationType,
            cancelExplanationDetail: explanationDetail
        });
    };
    _controllerMethods[CANCEL_INCOMPLETE_SIGNATURES] = (context, { explanationType, explanationDetail }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Attachments.CancelSignatures({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formId: formId.value,
            cancelAll: false,
            cancelExplanationType: explanationType,
            cancelExplanationDetail: explanationDetail
        });
    };
    _controllerMethods[RESEND_SIGNATURE_NOTIFICATIONS] = () => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Attachments.ResendSignatureNotifications({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formId: formId.value,
            formVersionId: formVersionId.value,
            useDraftStorage: _useDraftStorage.value
        });
    };
    _controllerMethods[REQUEST_SIGNATURES_V2] = (context, { envelopeDefinitionId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Envelopes.RequestSignatures({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeDefinitionId: envelopeDefinitionId,
            useDraftStorage: _useDraftStorage.value,
            draftReferenceId: formVersionId.value
        });
    };
    _controllerMethods[RESEND_ESIGNATURE_NOTIFICATIONS_V2] = (context, { envelopeId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Envelopes.ResendEnvelopeNotificationEmails({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeId: envelopeId,
            useDraftStorage: _useDraftStorage.value,
            draftReferenceId: formVersionId.value
        });
    };
    _controllerMethods[VOID_SIGNATURES_V2] = (context, { envelopeId, explanationType, explanationDetail }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Envelopes.VoidEnvelope({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeId: envelopeId,
            cancelExplanationType: explanationType,
            cancelExplanationDetail: explanationDetail
        });
    };
    _controllerMethods[FETCH_ENVELOPE_UPDATE] = (context, { envelopeId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Envelopes.FetchEnvelopeUpdate({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeId: envelopeId
        });
    };
    _controllerMethods[CREATE_WET_SIGN_ENVELOPE] = (context, { envelopeDefinitionId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _serviceControllers.Envelopes.CreateWetSignEnvelope({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeDefinitionId: envelopeDefinitionId,
            useDraftStorage: _useDraftStorage.value,
            draftReferenceId: formVersionId.value
        });
    };
    _controllerMethods[GENERATE_ENVELOPE_ITEM_DOCUMENT] = async (context, { envelopeItemDefinitionId }) => {
        if (_isReadOnly.value) {
            return;
        }

        const result = await _serviceControllers.Envelopes.GenerateEnvelopeItemDocument({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeItemDefinitionId: envelopeItemDefinitionId,
            useDraftStorage: _useDraftStorage.value,
            draftReferenceId: formVersionId.value
        });

        const document = result.Data.Document;

        const url =
            _page.DownloadDocumentUrl +
            `?ProgramId=${_page.ProgramId}` +
            `&${DOCUMENT_WRAPPER_TYPE.DocumentId}=${document.DocumentId}`;
        downloadFile(url, _anonParams);

        return result;
    };
    _controllerMethods[UPLOAD_WET_SIGN_ENVELOPE_ITEM] = (context, { file, envelopeId, envelopeItemId, captureXhr }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _queueSave(async () => {
            const data = {
                programId: _page.ProgramId,
                projectId: projectId.value,
                envelopeId: envelopeId,
                envelopeItemId: envelopeItemId,
                file
            };

            const result = await _page.sendXhr({
                url: _uploadWetSignEnvelopeItemUrl.value,
                data,
                headers: _page.getXhrHeaders(),
                captureXhr,
                contentType: XHR_CONTENT_TYPE.FormData
            });

            return result.Data;
        });
    };
    _controllerMethods[REMOVE_WET_SIGN_ENVELOPE_ITEM] = (context, { envelopeId, envelopeItemId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _queueSave(async () => {
            const result = await _serviceControllers.Envelopes.RemoveWetSignEnvelopeItem({
                programId: _page.ProgramId,
                projectId: projectId.value,
                envelopeId: envelopeId,
                envelopeItemId: envelopeItemId
            });

            return result.Data;
        });
    };
    _controllerMethods[VIEW_ENVELOPE_ITEM] = async (context, { envelopeItemDefinitionId }) => {
        const urlResult = await _serviceControllers.Envelopes.ViewEnvelopeItem({
            programId: _page.ProgramId,
            envelopeItemDefinitionId: envelopeItemDefinitionId,
            projectId: projectId.value
        });

        downloadFile(urlResult.Data.Url, _anonParams);
    };
    _controllerMethods[VIEW_COMPLETED_ENVELOPE_ATTACHMENT] = async (context, { attachmentId }) => {
        const urlResult = await _serviceControllers.Envelopes.ViewCompletedEnvelopeAttachment({
            programId: _page.ProgramId,
            projectId: projectId.value,
            attachmentId: attachmentId
        });

        downloadFile(urlResult.Data.Url, _anonParams);
    };
    _controllerMethods[VIEW_WET_SIGN_ENVELOPE_ITEM] = async (context, { envelopeId, envelopeItemId }) => {
        const urlResult = await _serviceControllers.Envelopes.ViewWetSignEnvelopeItem({
            programId: _page.ProgramId,
            envelopeId: envelopeId,
            envelopeItemId: envelopeItemId
        });

        downloadFile(urlResult.Data.Url, _anonParams);
    };
    _controllerMethods[COMPLETE_WET_SIGN_ENVELOPE] = (context, { envelopeId }) => {
        if (_isReadOnly.value) {
            return;
        }
        return _queueSave(async () => {
            const result = await _serviceControllers.Envelopes.CompleteWetSignEnvelope({
                programId: _page.ProgramId,
                projectId: projectId.value,
                envelopeId: envelopeId,
                useDraftStorage: _useDraftStorage.value,
                draftReferenceId: formVersionId.value
            });

            return result.Data;
        });
    };
    _controllerMethods[START_OVER_ENVELOPE] = async (context, { envelopeId }) => {
        if (_isReadOnly.value) {
            return;
        }
        await _serviceControllers.Envelopes.StartOverEnvelope({
            programId: _page.ProgramId,
            projectId: projectId.value,
            envelopeId: envelopeId
        });
    };
    _controllerMethods[CALL_WEB_ADAPTER] = async (context, { elementId, customizationId, configurationId }) => {
        if (_isReadOnly.value) {
            return;
        }
        try {
            _page.blockUi("Processing...");
            await dispatchSaveFormData(context, null);
            return await _editProjectController.value.CallConfiguredCustomization({
                programId: _page.ProgramId,
                projectId: projectId.value,
                elementId,
                customizationId,
                configurationId,
                useDraft: _useDraftStorage.value
            });
        } finally {
            _page.unBlockUi();
        }
    };
    _controllerMethods[RETRIEVE_SENSITIVE_DATA] = async (context, { elementId }) => {
        return await _serviceControllers.SensitiveData.RetrieveSensitiveProjectData2({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formVersionId: formVersionId.value,
            formElementId: elementId,
            useDraftStorage: _useDraftStorage.value
        });
    };
    _controllerMethods[UPLOAD_TABLE_FILE] = async (context, { file, formElementId, captureXhr }) => {
        const data = {
            programId: _page.ProgramId,
            formVersionId: formVersionId.value,
            formElementId,
            file
        };

        const result = await _page.sendXhr({
            url: _uploadTableFileUrl.value,
            data,
            headers: _page.getXhrHeaders(),
            captureXhr,
            contentType: XHR_CONTENT_TYPE.FormData
        });

        return result.Data;
    };
    _controllerMethods[IMPORT_PROJECT_TABLE_ROWS] = async (context, { formElementId, documentId }) => {
        await dispatchSaveFormData();
        return await _serviceControllers.ProjectTable.ImportNewRows({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formVersionId: formVersionId.value,
            formElementId,
            documentId,
            useDraftStorage: _useDraftStorage.value
        });
    };
    _controllerMethods[CANCEL_PROJECT_TABLE_IMPORT] = async (context, { operationId }) => {
        return await _serviceControllers.ProjectTable.CancelImport({
            programId: _page.ProgramId,
            projectId: projectId.value,
            operationId: operationId
        });
    };
    _controllerMethods[ADD_PROJECT_TABLE_ROW] = async (context, { formToOpenSignalId }) => {
        await dispatchSaveFormData();
        return await _serviceControllers.ProjectTable.AddProjectTableRow2({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formToOpenSignalId: formToOpenSignalId,
            formVersionId: formVersionId.value,
            useDraftStorage: _useDraftStorage.value
        });
    };
    _controllerMethods[GET_PROJECT_TABLE_DATA] = async (context, { groupId, formToOpenSignalId }) => {
        return await _serviceControllers.ProjectTable.GetProjectTableData2({
            programId: _page.ProgramId,
            projectId: projectId.value,
            groupId: groupId,
            formToOpenSignalId: formToOpenSignalId,
            useDraftStorage: _useDraftStorage.value,
            formVersionId: formVersionId.value
        });
    };
    _controllerMethods[EDIT_PROJECT_TABLE_ROW] = async (context, { formToOpenSignalId, projectRelationId }) => {
        await dispatchSaveFormData();
        return await _serviceControllers.ProjectTable.EditProjectTableRow2({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formToOpenSignalId: formToOpenSignalId,
            formVersionId: formVersionId.value,
            projectRelationId: projectRelationId,
            useDraftStorage: _useDraftStorage.value
        });
    };
    _controllerMethods[VIEW_PROJECT_TABLE_ROW] = async (context, { formToOpenSignalId, projectRelationId }) => {
        return await _serviceControllers.ProjectTable.ViewProjectTableRow({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formToOpenSignalId: formToOpenSignalId,
            projectRelationId: projectRelationId
        });
    };
    _controllerMethods[REMOVE_PROJECT_TABLE_ROW] = async (context, { projectRelationId }) => {
        return await _serviceControllers.ProjectTable.RemoveProjectTableRow({
            programId: _page.ProgramId,
            projectId: projectId.value,
            projectRelationId: projectRelationId
        });
    };
    _controllerMethods[FIND_PROJECT_TABLE_EXISTING_PROJECT] = async (
        context,
        { formElementId, formToOpenSignalId, projectNumber }
    ) => {
        return await _serviceControllers.ProjectTable.LookupExistingProjectData({
            programId: _page.ProgramId,
            formElementId: formElementId,
            formToOpenSignalId: formToOpenSignalId,
            projectNumber: projectNumber
        });
    };
    _controllerMethods[ADD_PROJECT_TABLE_EXISTING_PROJECT] = async (
        context,
        { formToOpenSignalId, existingProjectId }
    ) => {
        return await _serviceControllers.ProjectTable.AddExistingProjectRow({
            programId: _page.ProgramId,
            projectId: projectId.value,
            formToOpenSignalId: formToOpenSignalId,
            existingProjectId: existingProjectId,
            useDraftStorage: _useDraftStorage.value,
            formVersionId: formVersionId.value
        });
    };
    _controllerMethods[GET_CUSTOM_LIST_DATA_FULL] = async (context, { groupId, projectId }) => {
        return await _serviceControllers.CustomList.GetFullCustomListData({
            programId: _page.ProgramId,
            groupId,
            projectId
        });
    };
    _controllerMethods[GET_CUSTOM_LIST_DATA_INITIAL] = async (context, { groupId, selectors }) => {
        return await _serviceControllers.CustomList.GetInitialCustomListData({
            programId: _page.ProgramId,
            groupId: groupId,
            selectors: selectors
        });
    };
    _controllerMethods[GET_CUSTOM_LIST_DATA_CHILDREN] = async (context, { groupId, selectors }) => {
        return await _serviceControllers.CustomList.GetCustomListDataChildren({
            programId: _page.ProgramId,
            groupId: groupId,
            selectors: selectors
        });
    };
    _controllerMethods[GET_ADDRESS_AUTOCOMPLETE] = async (context, { suggestion, useLocationFilter }) => {
        return await _serviceControllers.AddressAutocomplete.GetAddressAutocomplete({
            programId: _page.ProgramId,
            suggestion: suggestion,
            useLocationFilter: useLocationFilter
        });
    };
    _controllerMethods[GET_ADDRESS_EXPANSION_AUTOCOMPLETE] = async (context, { suggestion }) => {
        return await _serviceControllers.AddressAutocomplete.GetAddressExpansionAutocomplete({
            programId: _page.ProgramId,
            suggestion: suggestion
        });
    };
    _controllerMethods[GET_ADDRESS_COUNTY_AUTOCOMPLETE] = async (context, { suggestion }) => {
        return await _serviceControllers.AddressAutocomplete.GetAddressCountyAutocomplete({
            programId: _page.ProgramId,
            suggestion: suggestion
        });
    };
    _controllerMethods[GET_LOCATION_SAS_TOKEN] = async () => {
        if (_sasToken.value) {
            return _sasToken.value;
        }
        if (!_sasTokenPromise.value) {
            _sasTokenPromise.value = _serviceControllers.Location.GetSASToken({
                programId: _page.ProgramId
            });
        }
        try {
            const result = await _sasTokenPromise.value;
            if (result.status === "success") {
                _sasToken.value = result.Data.SASToken;
            }
        } finally {
            // Clear the promise so a 504 won't prevent retries
            _sasTokenPromise.value = null;
        }
        return _sasToken.value;
    };
    _controllerMethods[SHOW_MFA_DIALOG] = () => {
        _mfaRequiredDialog.value.show();
    };
    _controllerMethods[ADMIN_CONFIRM_SAVE_FORM_DATA] = async () => {
        if (inAdminMode.value) {
            await _adminConfirmSaveFormData();
        }
    };

    const editProjectBase = {
        adminSaveConfirmed: readonly(adminSaveConfirmed),
        canPromoteForm: readonly(canPromoteForm),
        stripeLoadPromise: readonly(stripeLoadPromise),
        wistiaLoadPromise: readonly(wistiaLoadPromise),
        duringSubmit: readonly(duringSubmit),
        projectId: readonly(projectId),
        formVersionId: readonly(formVersionId),
        formId: readonly(formId),
        inAdminMode: readonly(inAdminMode),
        hasAnyDefaultToApply: readonly(hasAnyDefaultToApply),
        projectSaveState: readonly(projectSaveState),
        projectFormRef,
        mfaRequiredDialogRef,
        setEditProjectController,
        onEditProjectModelLoaded,
        dispatchSaveFormData,
        beginLoadNewForm,
        onNewFormLoaded,
        setValidationErrors
    };

    _vueRootApp.provide("ProjectId", readonly(projectId));
    _vueRootApp.provide("baseController", editProjectBase);
    _vueRootApp.provide("useEmbed", useEmbed);

    return editProjectBase;
}
